import { Plus } from 'lucide-react';
import { forwardRef, useCallback, useRef, type ReactNode } from 'react';

/** Everything a keyboard can land on, minus the things a row is never entered AT. */
const FOCUSABLE = [
  'input:not([type="hidden"]):not(:disabled)',
  'textarea:not(:disabled)',
  'select:not(:disabled)',
  '[contenteditable="true"]',
  'button:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** The field a teacher means to type in, as opposed to the row's drag handle or its bin. */
const EDITABLE = [
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not(:disabled)',
  'textarea:not(:disabled)',
  '[contenteditable="true"]',
].join(',');

/**
 * FOCUS THE ROW THE BUTTON JUST MADE.
 *
 * Reported 2026-07-29 and still true in October: "after clicking an add-item control, focus
 * stays on the add button instead of moving to the newly created input". Measured again on
 * MCQ — the option appears, `document.activeElement` is still Add Option — so a keyboard user
 * has to go looking for the field they just asked for, in a list whose length they changed.
 *
 * Done HERE because every repeatable list in the app grows through this one button, and the
 * twenty call sites hold twenty different row shapes: options, blanks, bins, conditions,
 * variables, dataviz series. Wiring each would be twenty chances to do it differently, and the
 * ones nobody got to would be exactly the new ones — the argument this component already makes
 * about its own styling.
 *
 * It watches for the row rather than being told about it. A `MutationObserver` over the commit
 * the click causes is the only thing that knows which node appeared without the button being
 * handed a ref per call site, and it is honest about the two cases that are not an append: a
 * row that mounts its field in a later effect (rich text does) is caught because the watch
 * runs until it finds a candidate, and an add that opens a dialog instead is left alone by the
 * guard below.
 *
 * THE GUARD IS WHAT MAKES IT SAFE: focus moves only if the button still has it. Anything that
 * takes focus for itself — a modal, a call site that already does this properly, the teacher
 * clicking elsewhere mid-flight — has already won by the time the row lands.
 */
function useFocusAddedRow(buttonRef: React.RefObject<HTMLButtonElement | null>) {
  const timer = useRef<number | null>(null);
  return useCallback((run: () => void) => {
    const button = buttonRef.current;
    if (!button || typeof MutationObserver === 'undefined') { run(); return; }

    /* The last thing WE moved focus to, so a later upgrade can tell its own work from the
       teacher's. See the two-step below. */
    let ours: HTMLElement | null = null;

    const take = (target: HTMLElement) => {
      target.focus();
      /*
       * ASK WHETHER IT TOOK, rather than predicting it would. A hidden or not-yet-laid-out
       * control refuses focus silently; the first version screened those out with
       * `getClientRects().length`, which is EMPTY FOR EVERYTHING in jsdom — so the feature was
       * off under test and on in the browser, the worst of both. Reading `activeElement` back
       * works in either, and the caller tries the next candidate when it did not take.
       */
      if (document.activeElement !== target) return false;
      ours = target;
      return true;
    };

    const observer = new MutationObserver((records) => {
      /*
       * Someone else has taken it, and their claim is better than ours by definition — a modal
       * the add opened, a call site that does this itself, the teacher clicking away. `ours` is
       * exempt because the step below deliberately moves focus twice.
       */
      const held = document.activeElement;
      if (held !== button && held !== ours) { stop(); return; }
      const added = records.flatMap(r => [...r.addedNodes]).filter(
        (n): n is HTMLElement => n.nodeType === 1 && n !== button && !n.contains(button));
      for (const node of added) {
        const within = (sel: string) => [
          ...(node.matches(sel) ? [node] : []), ...node.querySelectorAll<HTMLElement>(sel),
        ];
        /*
         * LAND NOW, UPGRADE IF THE FIELD TURNS UP — and the upgrade is what makes it right on
         * the block a teacher uses most. An MCQ option leads with its correct/incorrect
         * control and its text is a rich-text editor, which TipTap mounts an effect LATER: the
         * first commit offers only the role button, so settling there left a teacher on a
         * toggle rather than in the field they meant to type in. Measured on MCQ.
         *
         * So a field wins outright and ends the watch; anything else is taken straight away to
         * keep the landing immediate, and the watch runs on in case a field is still coming.
         */
        const editable = within(EDITABLE);
        for (const target of editable) if (take(target)) { stop(); return; }
        if (ours) continue;
        for (const target of within(FOCUSABLE)) if (take(target)) break;
      }
    });
    const stop = () => {
      observer.disconnect();
      if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
    };

    observer.observe(document.body, { childList: true, subtree: true });
    /* A ceiling, not a schedule: nothing here waits for it when the row arrives on the next
       commit, which is almost always. It is what stops a click that adds nothing — a disabled
       path, a cap already reached — leaving an observer on the document. */
    timer.current = window.setTimeout(stop, 500);
    run();
  }, [buttonRef]);
}

/**
 * The "add another row" affordance at the bottom of a repeatable list — answer options, ordering
 * items, targeted answers, variables, flow rules, dataviz rows.
 *
 * EVERY such control in the app renders through this. There is no second implementation and no
 * reason to write one: the treatment had drifted into four variants — the canonical one, the
 * canonical one plus disabled states, a centred muted pill in Numerical and Slider, and this
 * component — for a control that does one thing everywhere it appears.
 *
 * The adoption used to be deliberately incremental, block by block as each audit reached it. That
 * was the right call while the treatment was still being settled; it is not any more, because the
 * blocks that had not been reached were the ones diverging, and the divergence was reaching new
 * code faster than the audits were reaching blocks.
 */
export const AddRowButton = forwardRef<HTMLButtonElement, {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Points at the element explaining WHY it is disabled — Ordering's circular-item cap notice.
   *  A disabled control with no reason is the one case a screen-reader user cannot work out. */
  describedBy?: string;
  /**
   * Sized to its own text and set beside its siblings, instead of a full-width row of its own.
   *
   * For a set of adds that are ALTERNATIVES rather than a list's one way to grow — Flow's condition
   * kinds, where "Add Attempts" and "Add Feedback" are two different things you might add next.
   * Stacking those as two full-width dashed rows reads as two separate lists.
   */
  inline?: boolean;
  /**
   * Replaces the plus with the symbol of the thing being added, where the kind matters as much as
   * the act. The label still carries the verb, so nothing is lost to a symbol nobody knows yet.
   */
  icon?: ReactNode;
  /**
   * The symbol ALONE, with `label` carried by `aria-label` and `title` instead of rendered.
   *
   * For a row of adds that appears over and over — the variables panel shows one per section and
   * one inside every set, and four worded buttons do not fit a 350px panel repeated that often.
   * It is only legible because the glyphs are not new by the time you meet them there: every
   * variable row already carries the same one. Nothing is lost to a reader or to a hover.
   */
  iconOnly?: boolean;
  /** Share a row equally with its siblings, rather than sizing to its own content. */
  grow?: boolean;
  /** Phone-sized tap target. */
  touch?: boolean;
  /**
   * The accessible name, when the visible `label` is abbreviated to fit.
   *
   * A button reading "Set" in a cramped row still has to announce "Add a set": the convention is
   * that a control's name carries the verb, and "Set" alone names a thing rather than an act.
   */
  ariaLabel?: string;
  /**
   * Leave focus on the button rather than moving it into the row that appears.
   *
   * For an add whose new row is not a thing you type into next. A call site that moves focus
   * ITSELF needs no flag — see the guard in `useFocusAddedRow`, which stands aside for it.
   */
  keepFocus?: boolean;
}>(function AddRowButton({ label, onClick, disabled, describedBy, inline, icon, iconOnly, grow, touch, ariaLabel, keepFocus }, ref) {
  /* Our own handle on the node as well as the caller's: the watch needs to ask whether this
     button still holds focus, and a forwarded ref may be a callback or absent entirely. */
  const self = useRef<HTMLButtonElement | null>(null);
  const focusAddedRow = useFocusAddedRow(self);
  return (
    <button
      type='button'
      ref={(node) => {
        self.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      onClick={keepFocus ? onClick : () => focusAddedRow(onClick)}
      disabled={disabled}
      aria-describedby={describedBy}
      aria-label={ariaLabel ?? (iconOnly ? label : undefined)}
      title={ariaLabel ?? (iconOnly ? label : undefined)}
      /* `inline` runs TIGHTER than the others — `px-2 gap-1.5` rather than `px-3 gap-2`.
         Inline adds are a SET sharing one row, so their padding is competing for the row's width
         rather than giving one button presence; three of them at the roomier spacing wrapped in
         Flow's 370px panel, and a wrapped set of alternatives reads as two groups. */
      className={`flex items-center justify-center rounded-lg border border-dashed border-ws-border bg-transparent font-medium text-ws-text hover:border-ws-primary hover:text-ws-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-ws-border disabled:hover:text-ws-text ${
        iconOnly ? `gap-2 ${touch ? 'py-3' : 'py-2'} text-xs [&_svg]:h-4 [&_svg]:w-4`
        : inline ? 'gap-1.5 px-2 py-1.5 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5'
        : 'w-full gap-2 py-3 text-sm [&_svg]:h-4 [&_svg]:w-4'
      } ${grow ? 'min-w-0 flex-1' : ''}`}
    >
      {icon ?? <Plus aria-hidden='true' />}{iconOnly ? null : label}
    </button>
  );
});
